import { useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useUIStore } from '../../lib/stores/ui-store';
import { useAuthStore } from '../../lib/stores/auth-store';
import { useOrganizationStore } from '../../lib/stores/organization-store';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { OrganizationSwitcher } from '../dev';
import { RouteGuard } from '../common/route-guard';
import { cn } from '../../lib/utils';
import { isRunningInWebView } from '../../lib/mobile-bridge';
import { useGetOrganizations } from '../../lib/api/organization';
import { mapApiOrganizationToEntity } from '../../lib/mappers';
import { routerPaths } from '../../lib/router-paths';
import { GLOBAL_SHORTCUTS, SETTINGS_TAB_SHORTCUTS } from '../../lib/config/keyboard-shortcuts';
import { useMyPendingInvitations } from '../../lib/api/members';
import { MAINTENANCE_ROLES } from '../../types/enums';

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileWebView = isRunningInWebView();

  const { sidebarCollapsed } = useUIStore();
  const { isAuthenticated, } = useAuthStore();
  const { organization, setOrganization, setOrganizations } = useOrganizationStore();
  // Global keyboard shortcuts — use capture phase to intercept before browser defaults
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      const mod = e.metaKey || e.ctrlKey;

      // Check global shortcuts (Cmd/Ctrl based)
      for (const shortcut of GLOBAL_SHORTCUTS) {
        const keyMatch = e.key.toLowerCase() === shortcut.key;
        if (keyMatch && mod === shortcut.modifiers.mod && e.shiftKey === shortcut.modifiers.shift && e.altKey === shortcut.modifiers.alt) {
          e.preventDefault();
          e.stopPropagation();
          navigate(shortcut.path);
          return;
        }
      }

      // Check settings tab shortcuts (Alt/Option + digit, use e.code for digit keys)
      for (const shortcut of SETTINGS_TAB_SHORTCUTS) {
        const keyMatch = e.code === shortcut.key;
        const modMatch = !shortcut.modifiers.mod || mod;
        if (keyMatch && e.altKey === shortcut.modifiers.alt && e.shiftKey === shortcut.modifiers.shift && modMatch) {
          e.preventDefault();
          e.stopPropagation();
          navigate(shortcut.path);
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [navigate]);

  // Check onboarding status
  const { data: organizations, isLoading: orgsLoading } = useGetOrganizations(isAuthenticated)
  const { data: invitations, isLoading: invitationsLoading } = useMyPendingInvitations();

  // Auto-set organization and handle navigation after both queries resolve
  useEffect(() => {
    // Wait for both queries to finish loading
    if (!organizations || orgsLoading || invitationsLoading) return;

    const mappedOrgs = organizations.map(org => mapApiOrganizationToEntity(org));
    setOrganizations(mappedOrgs);

    // Filter out driver-only organizations
    const validOrganizations = organizations.filter(org => !org.roles?.every((r: string) => r === 'driver'));

    if (organization) {
      // If we already have an org (e.g. placeholder from mobile bridge),
      // update it with the full API data including correct roles
      const matchedOrg = validOrganizations.find(org => org.unid === organization.id);
      if (matchedOrg) {
        const mapped = mapApiOrganizationToEntity(matchedOrg);
        if (JSON.stringify(organization.roles) !== JSON.stringify(mapped.roles) || !organization.name) {
          setOrganization(mapped);
        }
      }
    } else if (validOrganizations.length > 0) {
      // No org selected — set first valid org
      const firstOrg = validOrganizations[0];
      if (firstOrg) {
        setOrganization(mapApiOrganizationToEntity(firstOrg));
      }
    }
    const hasOrgs = validOrganizations.length > 0 || !!organization;
    const isInvitationRoute = location.pathname.startsWith('/invitations');
    const isPendingInvitationsRoute = location.pathname.startsWith('/pending-invitations');
    const hasPendingInvites = invitations && invitations.length > 0;

    if (!hasOrgs && !isInvitationRoute && !isPendingInvitationsRoute) {
      navigate(hasPendingInvites ? routerPaths.pendingInvitations() : routerPaths.onboarding());
      return;
    }

    // Has orgs + pending invitations → redirect to invitations page (only on root)
    if (hasOrgs && hasPendingInvites && location.pathname === '/') {
      navigate(routerPaths.myInvitations());
      return;
    }

    // Auto-redirect based on role-restricted users
    if (validOrganizations.length > 0) {
      const currentOrg = organization ? validOrganizations.find(o => o.unid === organization.id) || validOrganizations[0] : validOrganizations[0];
      const roles = currentOrg?.roles?.map((r: string) => r) || [];

      const isMaintenanceOnly = roles.length > 0 && roles.every((r: string) => MAINTENANCE_ROLES.includes(r));
      const isFuelOperatorOnly = roles.length > 0 && roles.every((r: string) => r === 'fuel_operator');

      if (isMaintenanceOnly && !location.pathname.startsWith('/maintenance')) {
        navigate(routerPaths.maintenanceWorkOrders());
      } else if (isFuelOperatorOnly && !location.pathname.startsWith('/fuel')) {
        navigate(routerPaths.fuel());
      }
    }
  }, [organization, organizations, orgsLoading, invitations, invitationsLoading, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(routerPaths.login());
      return;
    }
  }, [isAuthenticated, location.pathname]);

  return (
    <div className="h-screen overflow-hidden bg-background">
      <Sidebar />
      {!isMobileWebView && <Header />}
      {/* Main content - no left padding on mobile/tablet, sidebar padding on desktop */}
      <main
        className={cn(
          'transition-all duration-300 h-screen overflow-hidden relative z-10',
          isMobileWebView ? 'pt-0' : 'pt-14',
          'pl-0',
          sidebarCollapsed ? 'min-[860px]:pl-16' : 'min-[860px]:pl-70'
        )}
      >
        <div className="p-4 lg:p-6 h-full overflow-y-auto">
          <RouteGuard><Outlet /></RouteGuard>
        </div>
      </main>
      <OrganizationSwitcher />
    </div>
  );
}
